import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCountryFlag } from "@/lib/countryFlags";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { toast } from "@/hooks/use-toast";
import { BarChart3, Users, CheckCircle, Trophy, MessageSquare, Mail, Send, AlertTriangle, ClipboardCheck, Bot } from "lucide-react";
import { format, formatDistanceToNow, isToday, isYesterday } from "date-fns";
import ResultsTab from "@/components/admin/ResultsTab";
import LlmTipsTab from "@/components/admin/LlmTipsTab";
import MatchupsTab from "@/components/admin/matchups/MatchupsTab";
import type { Matchup } from "@/components/admin/types";

const FEEDBACK_STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  inflight: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  done: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  wontfix: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const FEEDBACK_STATUS_LABELS: Record<string, string> = {
  new: "New",
  inflight: "In-flight",
  done: "Done",
  wontfix: "Won't Fix",
};

const FEEDBACK_STATUSES = ["new", "inflight", "done", "wontfix"] as const;

type Driver = {
  driver_number: number;
  full_name: string;
  name_acronym: string;
  team_name: string;
  team_colour: string | null;
  headshot_url: string | null;
  is_active: boolean | null;
};

type FeedbackItem = {
  id: string;
  user_id: string;
  message: string;
  status: string;
  created_at: string;
  display_name: string;
};

const Admin = () => {
  const queryClient = useQueryClient();
  const [selectedRaceId, setSelectedRaceId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("matchups");
  const [feedbackFilter, setFeedbackFilter] = useState<string>("new");
  const [expandedFeedbackId, setExpandedFeedbackId] = useState<string | null>(null);

  // Comms state
  const [notificationType, setNotificationType] = useState<"matchups_published" | "results_in" | "tips_reminder">("matchups_published");
  const [commsRaceIdOverride, setCommsRaceIdOverride] = useState<string | null>(null);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // ── Global KPIs ──
  const { data: kpis } = useQuery({
    queryKey: ["admin-kpis"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_landing_page_stats");
      if (error) throw error;
      return data as { tips_submitted: number; players: number; leagues_created: number };
    },
  });

  // ── Feedback ──
  const { data: feedback = [], isLoading: feedbackLoading } = useQuery({
    queryKey: ["admin-feedback"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_get_feedback");
      if (error) throw error;
      return (data ?? []) as FeedbackItem[];
    },
  });

  // ── Notification Log ──
  const { data: notificationLog = [], refetch: refetchLog } = useQuery({
    queryKey: ["admin-notification-log"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_get_notification_log");
      if (error) throw error;
      return (data ?? []) as { id: string; notification_type: string; race_id: string; sent_by: string; recipient_count: number; status: string; error_message: string | null; created_at: string | null }[];
    },
  });

  const { data: optedInCount } = useQuery({
    queryKey: ["admin-opted-in-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("email_notifications_enabled", true);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const handleSendNotification = async () => {
    if (!commsRaceId) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-notification", {
        body: { notification_type: notificationType, race_id: commsRaceId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Notification sent", description: `Sent to ${data.recipient_count} user${data.recipient_count === 1 ? "" : "s"}` });
      refetchLog();
    } catch (e: any) {
      toast({ title: "Failed to send", description: e.message, variant: "destructive" });
    } finally {
      setSending(false);
      setSendConfirmOpen(false);
    }
  };

  const updateFeedbackStatus = useMutation({
    mutationFn: async ({ feedbackId, status }: { feedbackId: string; status: string }) => {
      const { error } = await supabase.rpc("admin_update_feedback_status", {
        p_feedback_id: feedbackId,
        p_status: status,
      });
      if (error) throw error;
    },
    onMutate: async ({ feedbackId, status }) => {
      await queryClient.cancelQueries({ queryKey: ["admin-feedback"] });
      const prev = queryClient.getQueryData<FeedbackItem[]>(["admin-feedback"]);
      queryClient.setQueryData<FeedbackItem[]>(["admin-feedback"], (old) =>
        old?.map((f) => (f.id === feedbackId ? { ...f, status } : f)) ?? []
      );
      return { prev };
    },
    onError: (e: any, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(["admin-feedback"], context.prev);
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
  });

  const formatRelativeTime = useCallback((dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return formatDistanceToNow(date, { addSuffix: false }).replace("about ", "") + " ago";
    if (isYesterday(date)) return "yesterday";
    return format(date, "dd MMM");
  }, []);

  const filteredFeedback = feedbackFilter === "all" ? feedback : feedback.filter((f) => f.status === feedbackFilter);

  // ── Races ──
  const { data: races = [] } = useQuery({
    queryKey: ["admin-races"],
    queryFn: async () => {
      const { data: season } = await supabase.from("seasons").select("id").eq("is_current", true).single();
      if (!season) return [];
      const { data } = await supabase.from("races").select("*").eq("season_id", season.id).order("round_number");
      return data ?? [];
    },
  });

  const activeRaces = useMemo(() => races.filter((r) => r.status !== "cancelled"), [races]);

  const effectiveRaceId = useMemo(() => {
    if (selectedRaceId) return selectedRaceId;
    const now = new Date().toISOString();
    const upcoming = activeRaces.find((r) => r.date_end >= now);
    return upcoming?.id ?? activeRaces[0]?.id ?? null;
  }, [selectedRaceId, activeRaces]);

  const selectedRace = races.find((r) => r.id === effectiveRaceId);

  // Smart race selection for Comms tab based on notification type
  const commsRaceId = useMemo(() => {
    if (commsRaceIdOverride) return commsRaceIdOverride;
    if (activeRaces.length === 0) return null;
    const now = new Date().toISOString();
    if (notificationType === "results_in") {
      // Most recently completed race (date_end < now, latest first)
      const completed = [...activeRaces].reverse().find((r) => r.date_end < now);
      return completed?.id ?? activeRaces[0]?.id ?? null;
    }
    // matchups_published / tips_reminder → next upcoming race
    const upcoming = activeRaces.find((r) => r.date_end >= now);
    return upcoming?.id ?? activeRaces[activeRaces.length - 1]?.id ?? null;
  }, [commsRaceIdOverride, notificationType, activeRaces]);

  const commsSelectedRace = races.find((r) => r.id === commsRaceId);

  const { data: drivers = [] } = useQuery({
    queryKey: ["admin-drivers"],
    queryFn: async () => {
      const { data: season } = await supabase.from("seasons").select("id").eq("is_current", true).single();
      if (!season) return [];
      const { data } = await supabase.from("drivers").select("driver_number, full_name, name_acronym, team_name, team_colour, headshot_url, is_active").eq("season_id", season.id).order("full_name");
      return (data ?? []) as Driver[];
    },
  });

  const { data: matchups = [], isLoading: matchupsLoading } = useQuery({
    queryKey: ["admin-matchups", effectiveRaceId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_get_matchups", { p_race_id: effectiveRaceId! });
      if (error) throw error;
      return (data ?? []) as Matchup[];
    },
    enabled: !!effectiveRaceId,
  });

  const { data: stats } = useQuery({
    queryKey: ["admin-stats", effectiveRaceId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_get_race_pick_progress", { p_race_id: effectiveRaceId! });
      if (error) throw error;
      const progress = (data ?? {}) as { complete_tips?: number; total_tips?: number };
      // Status counts are derived in MatchupsTab straight from the matchups
      // query — deriving them here raced with it and went stale on publish.
      return { completeUsers: progress.complete_tips ?? 0, totalTips: progress.total_tips ?? 0 };
    },
    // Only consumed post-scoring, so there is no reason to call it while a
    // round is still being built.
    enabled: !!effectiveRaceId && selectedRace?.status === "completed",
  });

  const { data: qualityStats } = useQuery({
    queryKey: ["admin-quality-stats", effectiveRaceId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_get_race_pick_quality", { p_race_id: effectiveRaceId! });
      if (error) throw error;
      if (!data) return null;

      const quality = data as unknown as {
        matchup_stats: { matchup_id: string; total: number; correct: number }[];
        high_score: number;
        total_matchups: number;
      };

      const matchupStats: { matchup_id: string; pct: number }[] = (quality.matchup_stats ?? []).map((s) => ({
        matchup_id: s.matchup_id,
        pct: s.total > 0 ? s.correct / s.total : 0,
      }));

      if (matchupStats.length === 0) return null;

      const avgPct = matchupStats.reduce((sum, m) => sum + m.pct, 0) / matchupStats.length;
      const hardest = matchupStats.reduce((min, m) => m.pct < min.pct ? m : min, matchupStats[0]);
      const easiest = matchupStats.reduce((max, m) => m.pct > max.pct ? m : max, matchupStats[0]);

      return { avgPct, hardest, easiest, highScore: quality.high_score, total: quality.total_matchups };
    },
    enabled: !!effectiveRaceId && selectedRace?.status === "completed",
  });

  // Lookups resolve against every driver in the season so past rounds still
  // render drivers who have since been stood down (injury, mid-season swap).
  const driverMap = useMemo(() => {
    const map: Record<number, Driver> = {};
    drivers.forEach((d) => (map[d.driver_number] = d));
    return map;
  }, [drivers]);

  // Matchup building and results entry only ever offer this weekend's entry list.
  const activeDrivers = useMemo(() => drivers.filter((d) => d.is_active), [drivers]);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["admin-matchups", effectiveRaceId] });
    queryClient.invalidateQueries({ queryKey: ["admin-stats", effectiveRaceId] });
    queryClient.invalidateQueries({ queryKey: ["admin-quality-stats", effectiveRaceId] });
  }, [queryClient, effectiveRaceId]);

  // Picks up a generation run that finished while the phone tab was backgrounded/suspended.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") invalidateAll();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [invalidateAll]);

  return (
    <div className="px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <h1 className="text-2xl font-bold md:text-3xl">Admin Panel</h1>

        {/* Global KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-lg font-bold">{kpis?.players ?? "—"}</p>
                <p className="text-[11px] text-muted-foreground">Players</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <CheckCircle className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-lg font-bold">{kpis?.tips_submitted ?? "—"}</p>
                <p className="text-[11px] text-muted-foreground">Tips</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <Trophy className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-lg font-bold">{kpis?.leagues_created ?? "—"}</p>
                <p className="text-[11px] text-muted-foreground">Leagues</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="matchups" className="flex-1 gap-1.5">
              <BarChart3 className="h-4 w-4" /> <span className="hidden sm:inline">Matchups</span>
            </TabsTrigger>
            <TabsTrigger value="results" className="flex-1 gap-1.5">
              <ClipboardCheck className="h-4 w-4" /> <span className="hidden sm:inline">Results</span>
            </TabsTrigger>
            <TabsTrigger value="tips" className="flex-1 gap-1.5">
              <Bot className="h-4 w-4" /> <span className="hidden sm:inline">Tips</span>
            </TabsTrigger>
            <TabsTrigger value="comms" className="flex-1 gap-1.5">
              <Mail className="h-4 w-4" /> <span className="hidden sm:inline">Comms</span>
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex-1 gap-1.5">
              <MessageSquare className="h-4 w-4" /> <span className="hidden sm:inline">Feedback</span>
              {feedback.filter((f) => f.status === "new").length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">
                  {feedback.filter((f) => f.status === "new").length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Shared Round Selector (Matchups / Results / Tips — Comms keeps its smart default) */}
          {["matchups", "results", "tips"].includes(activeTab) && (
            <Select value={effectiveRaceId ?? ""} onValueChange={setSelectedRaceId}>
              <SelectTrigger className="mt-2 w-full">
                <SelectValue placeholder="Select a round" />
              </SelectTrigger>
              <SelectContent>
                {activeRaces.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    Round {r.round_number} — {r.meeting_name} {getCountryFlag(r.country_name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* ── Matchups Tab ── */}
          <TabsContent value="matchups" className="space-y-4">
            <MatchupsTab
              race={selectedRace}
              drivers={drivers}
              activeDrivers={activeDrivers}
              driverMap={driverMap}
              matchups={matchups}
              matchupsLoading={matchupsLoading}
              stats={stats}
              qualityStats={qualityStats ?? null}
              kpisPlayers={kpis?.players}
              invalidateAll={invalidateAll}
            />
          </TabsContent>

          {/* ── Results & Scoring Tab ── */}
          <TabsContent value="results" className="space-y-4">
            {/* key: remount per round so fetched/edited results can never be
                committed against a different round than they were loaded for. */}
            {/* drivers: the full season roster, NOT activeDrivers — a driver who
                raced but has since been stood down (mid-season swap) must still be
                enterable, or their matchups resolve to nothing and score 0. */}
            <ResultsTab key={selectedRace?.id} race={selectedRace} driverMap={driverMap} drivers={drivers} />
          </TabsContent>

          {/* ── LLM Tips Tab ── */}
          <TabsContent value="tips" className="space-y-4">
            <LlmTipsTab race={selectedRace} driverMap={driverMap} matchups={matchups} />
          </TabsContent>

          {/* ── Comms Tab ── */}
          <TabsContent value="comms" className="space-y-4">
            {/* Send Notification */}
            <Card>
              <CardContent className="space-y-4 p-4">
                <h3 className="text-sm font-semibold">Send Notification</h3>
                <Select value={notificationType} onValueChange={(v) => { setNotificationType(v as "matchups_published" | "results_in" | "tips_reminder"); setCommsRaceIdOverride(null); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="matchups_published">Matchups Published</SelectItem>
                    <SelectItem value="results_in">Results Are In</SelectItem>
                    <SelectItem value="tips_reminder">Tips Reminder</SelectItem>
                  </SelectContent>
                </Select>

                {/* Race Selector */}
                <Select value={commsRaceId ?? ""} onValueChange={setCommsRaceIdOverride}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a round" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeRaces.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        Round {r.round_number} — {r.meeting_name} {getCountryFlag(r.country_name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Email Preview */}
                {commsSelectedRace && (
                  <Card className="bg-muted/50">
                    <CardContent className="p-3 space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Preview</p>
                      <p className="text-sm font-semibold">
                        {notificationType === "matchups_published"
                          ? `SEND IT: Round ${commsSelectedRace.round_number} matchups are live! ${getCountryFlag(commsSelectedRace.country_name)}`
                          : notificationType === "results_in"
                          ? `SEND IT: Round ${commsSelectedRace.round_number} results are in! ${getCountryFlag(commsSelectedRace.country_name)}`
                          : `SEND IT: Round ${commsSelectedRace.round_number} picks lock soon! ${getCountryFlag(commsSelectedRace.country_name)}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {notificationType === "matchups_published"
                          ? `The matchups for Round ${commsSelectedRace.round_number} are now live! Head over to SEND IT to lock in your picks before qualifying starts.`
                          : notificationType === "results_in"
                          ? `The results for Round ${commsSelectedRace.round_number} are in! Check your scores, see how you stack up on the leaderboard, and find out who nailed their picks.`
                          : `Qualifying is around the corner — make sure your tips are locked in before it's too late.`}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Duplicate warning */}
                {commsRaceId && notificationLog.some((l) => l.notification_type === notificationType && l.race_id === commsRaceId && l.status === "sent") && (
                  <div className="flex items-center gap-2 rounded-md bg-yellow-100 p-2 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <p className="text-xs">This notification has already been sent for this round.</p>
                  </div>
                )}

                <Button
                  className="w-full font-semibold"
                  onClick={() => setSendConfirmOpen(true)}
                  disabled={!commsRaceId || sending}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send to {optedInCount ?? "—"} opted-in users
                </Button>
              </CardContent>
            </Card>

            {/* Notification History */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">History</h3>
              {notificationLog.length === 0 ? (
                <p className="text-sm text-muted-foreground">No notifications sent yet.</p>
              ) : (
                notificationLog.map((log) => {
                  const logRace = races.find((r) => r.id === log.race_id);
                  return (
                    <Card key={log.id}>
                      <CardContent className="flex items-center justify-between p-3">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">
                              {log.notification_type === "matchups_published" ? "Matchups" : log.notification_type === "results_in" ? "Results" : "Reminder"}
                            </Badge>
                            <Badge className={`text-[10px] ${log.status === "sent" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"}`}>
                              {log.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {logRace ? `Round ${logRace.round_number} — ${logRace.meeting_name}` : "Unknown race"} · {log.recipient_count} recipient{log.recipient_count === 1 ? "" : "s"}
                          </p>
                        </div>
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                          {log.created_at ? formatRelativeTime(log.created_at) : "—"}
                        </span>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* ── Feedback Tab ── */}
          <TabsContent value="feedback" className="space-y-4">
            {/* Status Filter */}
            <Select value={feedbackFilter} onValueChange={setFeedbackFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({feedback.length})</SelectItem>
                {FEEDBACK_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {FEEDBACK_STATUS_LABELS[s]} ({feedback.filter((f) => f.status === s).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {feedbackLoading ? (
              <p className="text-sm text-muted-foreground">Loading feedback…</p>
            ) : filteredFeedback.length === 0 ? (
              <p className="text-sm text-muted-foreground">No feedback found.</p>
            ) : (
              <div className="space-y-2">
                {filteredFeedback
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .map((f) => {
                    const isExpanded = expandedFeedbackId === f.id;
                    const truncatedMsg = f.message.length > 80 ? f.message.slice(0, 80) + "…" : f.message;
                    return (
                      <Card key={f.id} className="overflow-hidden">
                        <div
                          className="px-4 py-3 cursor-pointer"
                          onClick={() => setExpandedFeedbackId(isExpanded ? null : f.id)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium truncate">{f.display_name ?? "Unknown"}</span>
                            <span className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
                              {f.created_at ? formatRelativeTime(f.created_at) : "—"}
                            </span>
                          </div>
                          <div className="flex items-end justify-between gap-2 mt-1">
                            <p className="text-xs text-muted-foreground truncate flex-1">
                              {isExpanded ? "" : truncatedMsg}
                            </p>
                            {!isExpanded && (
                              <Badge className={`text-[10px] shrink-0 ${FEEDBACK_STATUS_COLORS[f.status] ?? ""}`}>
                                {FEEDBACK_STATUS_LABELS[f.status] ?? f.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="px-4 pb-3 space-y-3">
                            <p className="text-sm text-foreground whitespace-pre-wrap">{f.message}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {FEEDBACK_STATUSES.map((s) => (
                                <button
                                  key={s}
                                  className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition-colors ${
                                    f.status === s
                                      ? FEEDBACK_STATUS_COLORS[s]
                                      : "bg-muted text-muted-foreground hover:bg-accent"
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (f.status !== s) updateFeedbackStatus.mutate({ feedbackId: f.id, status: s });
                                  }}
                                >
                                  {FEEDBACK_STATUS_LABELS[s]}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Notification Confirmation */}
      <AlertDialog open={sendConfirmOpen} onOpenChange={setSendConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send email notification?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send a "{notificationType === "matchups_published" ? "Matchups Published" : notificationType === "results_in" ? "Results Are In" : "Tips Reminder"}" email to {optedInCount ?? 0} opted-in user{(optedInCount ?? 0) === 1 ? "" : "s"} for{" "}
              {commsSelectedRace ? `Round ${commsSelectedRace.round_number} — ${commsSelectedRace.meeting_name}` : "this round"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendNotification} disabled={sending}>
              {sending ? "Sending…" : "Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default Admin;

